import { useEffect, useRef, useState } from 'react';

const LAUNCH_DATE = new Date('2026-06-01T00:00:00');

function getTimeLeft() {
  const now = new Date();
  const diff = LAUNCH_DATE.getTime() - now.getTime();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

function CountdownBlock({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <div
        className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 flex items-center justify-center rounded-lg border border-[#00d4ff]/30 bg-black/60 backdrop-blur-sm"
        style={{ boxShadow: '0 0 20px rgba(0,212,255,0.15), inset 0 0 20px rgba(0,212,255,0.05)' }}
      >
        <span
          className="text-3xl sm:text-4xl md:text-5xl font-light text-white tabular-nums"
          style={{ textShadow: '0 0 20px rgba(0,212,255,0.6)' }}
        >
          {String(value).padStart(2, '0')}
        </span>
        <div className="absolute inset-0 rounded-lg bg-gradient-to-b from-[#00d4ff]/5 to-transparent pointer-events-none" />
      </div>
      <span className="mt-2 text-[10px] sm:text-xs tracking-[0.25em] uppercase text-gray-500">{label}</span>
    </div>
  );
}

export default function ComingSoonPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    setIsLoaded(true);

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const stars: Array<{ x: number; y: number; size: number; speed: number; opacity: number; twinkle: number }> = [];
    for (let i = 0; i < 200; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 1.8 + 0.2,
        speed: Math.random() * 0.2 + 0.05,
        opacity: Math.random() * 0.6 + 0.2,
        twinkle: Math.random() * Math.PI * 2,
      });
    }

    let animId: number;
    function animate() {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(star => {
        star.twinkle += 0.02;
        const op = star.opacity * (0.7 + 0.3 * Math.sin(star.twinkle));
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${op})`;
        ctx.fill();
        star.y += star.speed;
        if (star.y > canvas.height) { star.y = 0; star.x = Math.random() * canvas.width; }
      });
      animId = requestAnimationFrame(animate);
    }
    animate();

    const handleResize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    window.addEventListener('resize', handleResize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', handleResize); };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const body = new URLSearchParams({ email: email.trim() });
      const res = await fetch('https://readdy.ai/api/form/d6va89u7ipaugi04li10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
        setErrorMsg('Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  };

  return (
    <main className="relative w-full min-h-screen flex flex-col items-center justify-center overflow-hidden bg-black">
      {/* Starfield */}
      <canvas ref={canvasRef} className="absolute inset-0 z-0" />

      {/* Radial glow behind logo */}
      <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none">
        <div
          className="w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.08) 0%, transparent 70%)' }}
        />
      </div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70 z-10 pointer-events-none" />

      {/* Content */}
      <div
        className={`relative z-20 flex flex-col items-center px-6 w-full max-w-2xl transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        {/* Logo */}
        <div
          className={`mb-8 transition-all duration-1000 delay-200 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ animation: isLoaded ? 'float 6s ease-in-out infinite' : 'none' }}
        >
          <div className="relative">
            <div className="absolute inset-0 bg-[#00d4ff]/20 blur-3xl rounded-full scale-150 pointer-events-none" />
            <img
              src="https://public.readdy.ai/ai/img_res/d6ebd2a1-416d-4d41-8f5c-6f36fe395e24.png"
              alt="Motion Boyz"
              className="w-40 sm:w-52 md:w-64 h-auto relative z-10"
              style={{ filter: 'drop-shadow(0 0 30px rgba(0,212,255,0.7)) drop-shadow(0 0 60px rgba(0,212,255,0.3))' }}
            />
          </div>
        </div>

        {/* Tagline */}
        <div className={`text-center mb-4 transition-all duration-1000 delay-400 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p
            className="text-xs sm:text-sm tracking-[0.5em] uppercase text-[#00d4ff] font-light mb-3"
            style={{ textShadow: '0 0 20px rgba(0,212,255,0.8)' }}
          >
            Something Big Is Coming
          </p>
          <h1 className="text-white text-4xl sm:text-5xl md:text-6xl font-extralight tracking-[0.15em] uppercase mb-4">
            Coming Soon
          </h1>
          <p className="text-gray-400 text-sm sm:text-base font-light leading-relaxed max-w-md mx-auto">
            We&apos;re crafting something extraordinary. The next chapter of Motion Boyz drops soon — be the first to know.
          </p>
        </div>

        {/* Divider */}
        <div className={`w-24 h-px bg-gradient-to-r from-transparent via-[#00d4ff] to-transparent mb-8 transition-all duration-1000 delay-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} />

        {/* Countdown */}
        <div className={`flex gap-4 sm:gap-6 mb-10 transition-all duration-1000 delay-600 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <CountdownBlock value={timeLeft.days} label="Days" />
          <div className="flex items-center pb-5">
            <span className="text-[#00d4ff]/60 text-2xl font-light" style={{ textShadow: '0 0 10px rgba(0,212,255,0.4)' }}>:</span>
          </div>
          <CountdownBlock value={timeLeft.hours} label="Hours" />
          <div className="flex items-center pb-5">
            <span className="text-[#00d4ff]/60 text-2xl font-light" style={{ textShadow: '0 0 10px rgba(0,212,255,0.4)' }}>:</span>
          </div>
          <CountdownBlock value={timeLeft.minutes} label="Mins" />
          <div className="flex items-center pb-5">
            <span className="text-[#00d4ff]/60 text-2xl font-light" style={{ textShadow: '0 0 10px rgba(0,212,255,0.4)' }}>:</span>
          </div>
          <CountdownBlock value={timeLeft.seconds} label="Secs" />
        </div>

        {/* Email Signup */}
        <div className={`w-full max-w-lg transition-all duration-1000 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {status === 'success' ? (
            <div
              className="flex flex-col items-center gap-3 py-6 px-8 rounded-xl border border-[#00d4ff]/30 bg-black/40 backdrop-blur-sm"
              style={{ boxShadow: '0 0 30px rgba(0,212,255,0.1)' }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full border border-[#00d4ff]/50 text-[#00d4ff]" style={{ boxShadow: '0 0 20px rgba(0,212,255,0.3)' }}>
                <i className="ri-check-line text-2xl" />
              </div>
              <p className="text-white font-light tracking-wider">You&apos;re on the list!</p>
              <p className="text-gray-400 text-sm text-center">We&apos;ll hit you up the moment we drop. Stay ready.</p>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-3"
            >
              <p className="text-center text-gray-400 text-xs tracking-[0.2em] uppercase mb-1">Get Early Access</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  className="flex-1 bg-white/5 border border-white/10 focus:border-[#00d4ff]/60 outline-none text-white text-sm placeholder-gray-600 px-5 py-3.5 rounded-full transition-all duration-300 focus:shadow-[0_0_20px_rgba(0,212,255,0.15)] backdrop-blur-sm"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="group relative px-7 py-3.5 bg-[#00d4ff] rounded-full text-white text-sm font-light tracking-wider whitespace-nowrap overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(0,212,255,0.5)] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                >
                  <span className="relative z-10 flex items-center gap-2 justify-center">
                    {status === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Notify Me</span>
                        <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-full" />
                </button>
              </div>
              {errorMsg && (
                <p className="text-red-400/80 text-xs text-center">{errorMsg}</p>
              )}
              <p className="text-gray-600 text-xs text-center">No spam. Just the drop alert. Unsubscribe anytime.</p>
            </form>
          )}
        </div>

        {/* Divider */}
        <div className={`w-24 h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent my-8 transition-all duration-1000 delay-800 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} />

        {/* Social Links */}
        <div className={`flex items-center gap-5 transition-all duration-1000 delay-900 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {[
            { href: 'https://www.instagram.com/motionboyzwear/', icon: 'ri-instagram-line', label: 'Instagram' },
            { href: 'https://www.tiktok.com/@motion.boyz82', icon: 'ri-tiktok-line', label: 'TikTok' },
            { href: 'https://x.com/MotionBoyzwear', icon: 'ri-twitter-x-line', label: 'X (Twitter)' },
          ].map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              title={s.label}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-800 text-gray-500 hover:text-[#00d4ff] hover:border-[#00d4ff]/50 hover:shadow-[0_0_15px_rgba(0,212,255,0.3)] transition-all duration-300 cursor-pointer"
            >
              <i className={`${s.icon} text-lg`} />
            </a>
          ))}
        </div>

        {/* Footer note */}
        <p className={`mt-8 text-gray-700 text-xs tracking-wider transition-all duration-1000 delay-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
          © 2026 Motion Boyz — Motion Never Stops
        </p>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-14px); }
        }
      `}</style>
    </main>
  );
}
